import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Trash } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { Client } from '@freelancer-os/shared-types';
import { ClientTags } from './ClientTags';
import { useUpdateClient, useDeleteClient } from '../../../hooks/useClients';
import { toast } from 'sonner';

interface ClientHeaderProps {
  client: Client;
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

export function ClientHeader({ client }: ClientHeaderProps) {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  const { mutate: updateClient } = useUpdateClient(client.id);
  const { mutate: deleteClient, isPending: isDeleting } = useDeleteClient();

  const initials = getInitials(client.name);

  const handleAddTag = (tag: string) => {
    if (client.tags.includes(tag)) return;
    updateClient({ tags: [...client.tags, tag] });
  };

  const handleRemoveTag = (tag: string) => {
    updateClient({ tags: client.tags.filter((existing) => existing !== tag) });
  };

  const handleDelete = () => {
    if (!confirm(t('detail.deleteConfirm'))) return;
    deleteClient(client.id, {
      onSuccess: () => {
        toast.success(t('detail.delete'));
        navigate('/clients');
      },
    });
  };

  return (
    <div className="space-y-4">
      {/* Top nav */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/clients')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> {t('detail.backToList')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          disabled={isDeleting}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash className="w-4 h-4 mr-1" /> {t('detail.delete')}
        </Button>
      </div>

      {/* Header card */}
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-gradient-to-br from-brand-400 to-emerald-500 flex items-center justify-center text-white text-sm font-bold shrink-0 rounded">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-surface-900 leading-tight">{client.name}</h1>
          <p className="text-sm text-surface-500 mt-0.5">
            {[client.email, client.zaloPhone].filter(Boolean).join(' · ') || '—'}
          </p>
          <div className="mt-2">
            <ClientTags
              tags={client.tags}
              mode="edit"
              onAdd={handleAddTag}
              onRemove={handleRemoveTag}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
